<template>
  <div class="container py-5">

    <article 
      v-if="!warehousesLoading && warehouses.length == 0"
      class="message is-info is-fullwidth">
      <div class="message-body">
        There are no warehouses.
      </div>
    </article>

    <div class="tabs" v-if="!warehousesLoading && warehouses.length > 0">
      <ul>
        <li 
          v-for="warehouse in warehouses"
          @click="setFilter({
            field: 'warehouse_id', 
            value: warehouse.id
          })"
          :key="warehouse.id"
          :class="{ 'is-active': warehouse.id == filters.warehouse_id }">
          <a>{{ warehouse.warehouse_name }}</a>
        </li>
      </ul>
    </div>

    <div class="buttons pb-3">
      <button
        @click="managingWarehouses = true"
        class="button is-primary">
        Manage Warehouses
      </button>
      <button
        @click="adding = true"
        v-if="warehouses.length > 0"
        class="button is-primary">
        Add a Warehouse Space
      </button>
      <button 
        class="button"
        v-if="selected.length > 0"
        @click="launchConfirmDelete()">
        <span class="icon is-medium">
          <i class="fas fa-trash has-text-danger"></i>
        </span>
        <span>Delete {{ selected.length }} selected warehouse spaces</span>
      </button>
    </div>

    <template v-if="!loading && warehouses.length > 0">

      <article 
        v-if="!loading && locations.length == 0" 
        class="message is-info is-fullwidth">
        <div class="message-body">
          There are no spaces for this warehouse.
        </div>
      </article>

      <table 
        v-if="!loading && locations.length > 0"
        class="table is-fullwidth is-hoverable is-clickable">

        <thead>
          <tr>
            <th>
              <v-checkbox 
                :value="allSelected"
                @input="toggleAllSelected($event)"/>
            </th>
            <th 
              v-for="(header, headerIndex) in headers"
              :key="headerIndex">
              {{ header }}
            </th>
          </tr>
        </thead>

        <tbody>
          <template v-for="location in locations">
            <tr 
              @click="viewDetails(location)"
              class="v-centered"
              :key="location.id">

              <td>
                <v-checkbox 
                  :value="selected.includes(location.id)"
                  @input="toggleSelected(location.id)"/>
              </td>

              <!-- Not in edit mode -->
              <template>
                <td>
                  <span v-if="location.id != editingId">
                    {{ location.location_code }}
                  </span>
                  <v-text-field
                    v-else
                    required
                    placeholder="Location code"
                    @click.native.stop=""
                    v-model="location.location_code"/>
                </td>
                <!-- <td>
                  <span v-if="location.id != editingId">
                    {{ location.location_level }}
                  </span>
                  <v-text-field
                    v-else
                    placeholder="Shelf level"
                    @click.native.stop=""
                    v-model="location.location_level"/>
                </td> -->
                <td>
                  <span v-if="location.id != editingId">
                    {{ location.location_desc }}
                  </span>
                  <v-text-field
                    v-else
                    placeholder="Location description"
                    @click.native.stop=""
                    v-model="location.location_desc"/>
                </td>
                <!-- <td></td> -->
                <td>
                  {{ location.storages.length }}
                </td>
              </template>

              <td>
                <div class="buttons buttons-right">
                  <template v-if="location.id != editingId">
                    <button 
                      @click.stop="editingId = location.id"
                      class="button is-small has-text-grey">
                      <span class="icon">
                        <i class="fas fa-edit"></i>
                      </span>
                      <span>Edit</span>
                    </button>
                    <button 
                      @click.stop="launchConfirmDelete(location.id)"
                      class="button is-small has-text-grey">
                      <span class="icon">
                        <i class="fas fa-trash"></i>
                      </span>
                      <span>Delete</span>
                    </button>
                  </template>

                  <template v-else>
                    <button 
                      @click.stop="saveEdits(location)"
                      class="button is-small is-primary">
                      <span class="icon">
                        <i class="fas fa-save"></i>
                      </span>
                      <span>Save Edits</span>
                    </button>
                    <button 
                      @click.stop="editingId = null"
                      class="button is-small has-text-grey">
                      <span class="icon">
                        <i class="fas fa-times"></i>
                      </span>
                      <span>Exit Editing</span>
                    </button>
                  </template>
                </div>
              </td>

            </tr>

          </template>
          
        </tbody>

      </table>
    </template>

    <v-pagination 
      v-if="pagination.pages > 1"
      :value="pagination.page"
      @input="changePage"
      :length="pagination.pages"/>

    <add-warehouse-location-modal
      v-model="adding"
      :warehouse-id="filters.warehouse_id"/>

    <manage-warehouses-modal 
      v-model="managingWarehouses"/>
    
    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmedCb()"
      @cancel="deleteId = null; confirmDelete.state = false"/>

  </div>
</template>

<script>
import VCheckbox from '@/components/base/VCheckbox'
import VTextField from '@/components/base/VTextField'
import VConfirmModal from '@/components/base/VConfirmModal'
import VPagination from '@/components/base/VPagination'
import AddWarehouseLocationModal from '@/components/warehousing/AddWarehouseLocationModal'
import ManageWarehousesModal from '@/components/warehousing/ManageWarehousesModal'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: { 
    VCheckbox,
    VTextField,
    VConfirmModal, 
    VPagination,
    AddWarehouseLocationModal,
    ManageWarehousesModal
  },
  data() {
    return {
      headers: ['Space Code', 'Space Description', 'Number of Skids Stored', ''],
      editingId: null,
      adding: false,
      managingWarehouses: false,
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this warehouse space?',
        type: 'danger'
      },
      deleteConfirmedCb: null
    }
  },
  computed: {
    ...mapState("warehouses", {
      warehouses: state => state.warehouses,
      warehousesLoading: state => state.loading
    }),
    ...mapState("warehouseLocations", [
      "locations", "loading", "selected", "allSelected",
      "filters", "pagination"
    ])
  },
  watch: {
    'pagination.page'() {
      let params = {...this.pagination, ...this.filters};
      this.getLocations(params);
    },
    filters: {
      handler(filters) {
        this.getLocations(filters);
      },
      deep: true
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("warehouses", ["getWarehouses"]),
    ...mapMutations("warehouses", {
      setWarehousesLoading: "setLoading"
    }),
    ...mapActions("warehouseLocations", [
      "getLocations", "deleteLocation", "updateLocation", "toggleAllSelected"
    ]),
    ...mapMutations("warehouseLocations", [
      "toggleSelected", "changePage", "setFilter"
    ]),
    initialize() {
      if (this.warehouses.length == 0) {
        this.getWarehouses().then(() => {
          if (this.warehouses.length > 0) {
            this.setFilter({
              field: 'warehouse_id',
              value: this.warehouses[0].id
            })
          }
        })
      } else {
        this.setWarehousesLoading(false);
        this.setFilter({
          field: 'warehouse_id',
          value: this.warehouses[0].id
        })
      }
    },
    saveEdits(location) {
      this.updateLocation({
        locationId: location.id,
        data: {
          'location_level': location.location_level,
          'location_desc': location.location_desc,
          'location_code': location.location_code
        }
      }).then(() => this.editingId = null);
    },
    viewDetails(location) {
      this.$router.push({ 
        name: 'WarehouseLocationViewer',
        params: {
          locationId: location.id,
          location: location
        }
      })
    },
    launchConfirmDelete(id) {
      if (!id) {
        this.deleteConfirmedCb = () => {
          this.selected.forEach(id => {
            this.deleteLocation(id);
          })
        }
        this.confirmDelete.message = `Are you sure you want to delete the ${this.selected.length} spaces?`;
      } else {
        this.deleteConfirmedCb = () => {
          this.deleteLocation(id);
        }
        this.confirmDelete.message = 'Are you sure you want to delete this warehouse space?'
      }
      this.confirmDelete.state = true;
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
</style>